import { useMutation, useQuery } from "@apollo/client/react";
import { useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useNavigate, useParams } from "react-router";
import { CompanyLogo } from "@/pages/company/CompanyLogo";
import { useCompanyContext } from "@/pages/company/use-company-context";
import { BackButton } from "@/components/common/BackButton";
import { Button } from "@/components/design/Button";
import { buttonClasses } from "@/components/design/button-classes";
import { MenuItem } from "@/components/design/MenuItem";
import { NoBackend } from "@/components/common/NoBackend";
import { ShareIcon } from "@/components/share/ShareItemCard";
import type { ShareIconName } from "@/components/share/ShareItemCard";
import { ShareUrlButton } from "@/components/share/ShareUrlButton";
import { ShareItemAttachments } from "@/components/share/ShareItemAttachments";
import { SharedItemAuthor } from "@/components/share/SharedItemAuthor";
import { creatorName, fiveStarRounded, itemTypeLabel } from "@/lib/share-item";
import { formatDateTime } from "@/lib/format-date";
import { useAssistantContext } from "@/lib/use-assistant-context";
import { useAssistantTour } from "@/lib/use-assistant-tour";
import { logApplicationFailure, userErrorMessage } from "@/lib/user-error";
import {
  companyFrozenMessage,
  companyFrozenReaderMessage,
  mayManageItem,
  type ShareItemLike,
} from "@/lib/share-item";
import {
  CreateMatchDocument,
  MatchMode as GqlMatchMode,
  MyselfDocument,
  SharedItemAuthorAvatarDocument,
  TakeOverSharedItemDocument,
  ViewSharedItemDocument,
} from "@/graphql/generated/operations";
import { usePin } from "@/pages/save/use-pin";
import { NewMatchModal } from "@/components/match/NewMatchModal";
import type { NewMatchDraft } from "@/components/match/NewMatchModal";
import { dataToDesign } from "@/components/match/match-design-mapping";
import { EMPTY_MATCH_DATA } from "@/components/match/match-design-rules";
import { throwIfValidationRejected, useValidationFallback } from "@/components/match/use-match-editor";
import { useSharedItem } from "./use-shared-item";
import { useSharedItemComments } from "./use-shared-item-comments";

// SHARE · Discover - detail view of a shared item: meta + owner actions
// (edit/delete) on the left, content (title · image gallery · description)
// in the middle, comments on the right.
//
// "Interesse zeigen" toggles signedUp via signUpSharedProject; the comment menu's
// "Bearbeiten" edits inline via updateSharedItemComment. Watching is modelled
// through pinning (pinned = "Beobachtet"): the Watch button calls addPins /
// removePins on this item. watchSharedItem is intentionally unused.

const imgSrc = (f: { mimeType?: string | null; content?: string | null } | null | undefined): string | null =>
  f?.content ? `data:${f.mimeType || "image/jpeg"};base64,${f.content}` : null;

export function ShareItemDetailPage() {
  const intl = useIntl();
  const validationFallback = useValidationFallback();
  const { id: itemId = "" } = useParams();
  const navigate = useNavigate();
  const sharedItem = useSharedItem(itemId);
  const { item, loading, error, busy: deleting } = sharedItem;
  const author = item?.createdBy;
  // BOTH NAMES, TO EVERYONE WHO CAN SEE THE POST — the company above, the person who wrote it below.
  // That is concept v10 (0002 §6): "Beides, nicht statt … die Reihenfolge sagt, wessen Aussage es ist",
  // and the reasoning only works if both names are on the page.
  //
  // IT WAS ADMIN-ONLY FOR THREE DAYS. @adr SPARK-0042-company-post-detail-shows-company-and-operator@1 records the reversal of the rule that narrowed the
  // author to administrators of the owning company. Kevin reversed
  // that on 2026-08-31: "the author of a company's post should be visible to everyone able to see the
  // company who published it". So there is no viewer test here at all — `isAdminOfCompany` still
  // decides who may EDIT, and decides nothing about who is named.
  const publisher = item?.company?.id ?? "";
  const authorAvatar = useQuery(SharedItemAuthorAvatarDocument, {
    variables: { id: author?.id ?? "" },
    skip: !author?.id,
  });

  // Live assistant context (motive 1, @external-adr AGENT-SERVICE-0020-ag-ui-protocol-adoption): the shared item being viewed + the user's
  // relationship to it. Before the early returns below so hook order stays stable.
  useAssistantContext(
    item
      ? [
          {
            description: "the shared item the user is viewing",
            value:
              `"${item.name}" (${item.itemType})` +
              `; ${item.views ?? 0} views, ${item.signUps ?? 0} sign-ups, ${item.ratings ?? 0} ratings` +
              `; the user ${mayManageItem(item) ? "may manage this" : "may not manage this"}` +
              (item.pinned ? ", is watching it" : "") +
              (item.signedUp ? ", has signed up" : ""),
          },
        ]
      : null,
  );

  // Guided tour (/tour): this shared item — content, stats, and the action buttons. Same
  // hooks-before-early-returns placement as the context call above.
  useAssistantTour(
    item
      ? {
          label: intl.formatMessage({
            id: "share.itemDetail.tour.label",
            defaultMessage: "Share Post",
          }),
          summary: intl.formatMessage(
            {
              id: "share.itemDetail.tour.summary",
              defaultMessage:
                'You are looking at "{name}", a {type} from the Share community.',
            },
            { name: item.name ?? "–", type: itemTypeLabel(item.itemType) },
          ),
          stops: [
            {
              anchor: "share.item.content",
              title: intl.formatMessage({
                id: "share.itemDetail.tour.content.title",
                defaultMessage: "The content",
              }),
              description: intl.formatMessage(
                {
                  id: "share.itemDetail.tour.content.desc",
                  defaultMessage:
                    'Title, image gallery and full description of "{name}". Click the thumbnails to browse the images.',
                },
                { name: item.name ?? "–" },
              ),
            },
            {
              anchor: "share.item.stats",
              title: intl.formatMessage({
                id: "share.itemDetail.tour.stats.title",
                defaultMessage: "How it's doing",
              }),
              description: intl.formatMessage(
                {
                  id: "share.itemDetail.tour.stats.desc",
                  defaultMessage:
                    "The item's reach so far: {views} views, {pins} pins and {signUps} interested people.",
                },
                {
                  views: item.views ?? 0,
                  pins: item.watches ?? 0,
                  signUps: item.signUps ?? 0,
                },
              ),
            },
            {
              anchor: "share.item.actions",
              title: intl.formatMessage({
                id: "share.itemDetail.tour.actions.title",
                defaultMessage: "Get involved",
              }),
              description:
                !mayManageItem(item) && item.itemType === "PROJECT"
                  ? intl.formatMessage({
                      id: "share.itemDetail.tour.actions.projectDesc",
                      defaultMessage:
                        "Watch the item to follow it, express interest to sign up as a participant, or start a new match linked to this project.",
                    })
                  : intl.formatMessage({
                      id: "share.itemDetail.tour.actions.desc",
                      defaultMessage:
                        "Watch the item to follow it, or start a new match linked to it.",
                    }),
            },
          ],
        }
      : null,
  );

  const gallery: Array<{ __typename: "File"; mimeType: string | null; content: string }> = [
    item?.mainImage,
    ...(item?.images ?? []),
  ].filter(
    (f): f is { __typename: "File"; mimeType: string | null; content: string } =>
      Boolean(f?.content),
  );
  const [activeIdx, setActiveIdx] = useState(0);
  const hero = gallery[activeIdx] ?? null;

  const [confirmingDelete, setConfirmingDelete] = useState(false);
  // The one refusal worth naming here. Everything else already goes to the diagnostics log; a frozen
  // company is the only case where the user can actually do something about it.
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // Applying to and rating a frozen company's project are refused for EVERY caller, not only admins,
  // and those items stay listed on the company page — so an ordinary reader can reach the refusal.
  const [actionError, setActionError] = useState<string | null>(null);
  const [signingUp, setSigningUp] = useState(false);
  const [newMatchOpen, setNewMatchOpen] = useState(false);
  const [watching, setWatching] = useState(false);
  const pin = usePin();

  const [viewItem] = useMutation(ViewSharedItemDocument);
  const viewedRef = useRef(false);
  useEffect(() => {
    if (viewedRef.current || !itemId) return;
    viewedRef.current = true;
    // SWALLOWED ON PURPOSE, and it needs a catch rather than a bare `void`. Recording a view is a
    // side effect the reader never asked for, so a failure must not reach them — but since concept
    // v9 an INACTIVE company's item refuses this for EVERY caller, and those items stay reachable
    // because the company page still lists them. Without a handler that refusal became an unhandled
    // rejection on an ordinary page visit, which the runtime diagnostics then logged as an error.
    void viewItem({ variables: { input: { sharedItemId: itemId } } }).catch(() => {});
  }, [itemId, viewItem]);

  // Match-starten-from-SharedItem entry point per @adr SPARK-0006-project-as-conceptual-entity@1. The new Match is
  // Linked-from-birth + pre-copy: createMatch sets sharedItem, then
  // takeOverSharedItem performs the server-side copy of project.* — the same
  // mutation the in-match "Übernehmen" button uses, so behavior stays in lockstep.
  const [createMatch] = useMutation(CreateMatchDocument);
  const [takeOverSharedItem] = useMutation(TakeOverSharedItemDocument);
  async function startLinkedMatch(draft: NewMatchDraft) {
    if (!item) return;
    const result = await createMatch({
      variables: {
        input: {
          data: {
            sharedItem: { id: item.id },
            matchMode: draft.matchMode as GqlMatchMode,
            name: draft.name || null,
            description: draft.description || null,
            access: draft.access.map((a) => ({
              person: { id: a.personId },
              prepare: a.prepare,
              design: a.design,
              discover: a.discover,
              match: a.match,
            })),
            // Born-complete like the normal Match entry (MatchPage): teamProfile
            // + teamSize/teamCount present so the solver's non-null checks pass
            // on first solve without a prior design edit.
            design: dataToDesign(EMPTY_MATCH_DATA),
          },
        },
      },
    });
    const payload = result.data?.createMatch;
    throwIfValidationRejected(payload, "ValidateMatchResult", validationFallback);
    if (payload?.__typename !== "CreateMatchResult") return;
    const matchId = payload.result.id;
    await takeOverSharedItem({
      variables: { input: { matchId, sharedItemId: item.id } },
    });
    navigate(`/match/${matchId}`);
  }

  async function runDelete() {
    if (deleting || !item) return;
    setDeleteError(null);
    // The two states share one span, so each path clears the other: without this, a delete that
    // failed a minute ago keeps its sentence on screen while a later rating refusal is swallowed.
    setActionError(null);
    try {
      await sharedItem.remove();
      navigate("/extended-search?type=SHARE");
    } catch (e) {
      // EVERY refusal is shown, not only the actionable one. Naming the frozen-company case is the
      // point — it is the one the user can do something about — but falling through to nothing for
      // all the others recreated the very defect this catch was written for: a revoked membership or
      // a network blip left the Yes/No pair sitting there, re-enabled and unchanged, and the button
      // simply did not work. The page-level banner used to cover that and no longer does, because
      // `useSharedItem` now reports only the READ's error (a refused write must not blank the item).
      setDeleteError(companyFrozenMessage(intl, e) ?? userErrorMessage(intl, e));
      logApplicationFailure("share.delete-item");
    }
  }

  async function toggleWatch() {
    if (watching || !item) return;
    setWatching(true);
    try {
      if (item.pinned) {
        await pin.remove({ sharedItemIds: [item.id] });
      } else {
        await pin.add({ sharedItemIds: [item.id] });
      }
      await sharedItem.refetch();
    } catch {
      logApplicationFailure("share.toggle-watch");
    } finally {
      setWatching(false);
    }
  }

  async function toggleSignUp() {
    if (signingUp || !item) return;
    setSigningUp(true);
    setActionError(null);
    setDeleteError(null);
    try {
      await sharedItem.signUp(!item.signedUp);
    } catch (e) {
      // Same fall-through as the delete, with the READER's wording: signing up is not an admin act,
      // so the person who meets this refusal cannot reactivate anything and must not be told to.
      setActionError(companyFrozenReaderMessage(intl, e) ?? userErrorMessage(intl, e));
      logApplicationFailure("share.toggle-sign-up");
    } finally {
      setSigningUp(false);
    }
  }

  const authorName = creatorName(author) || "–";

  return (
    <main
      className="w-full max-w-[1600px] px-4 pb-12 pt-6 @sm:px-6 @lg:px-10"
      data-graphql="SharedItemDetail, SharedItemAuthorAvatar, SharedItemFileContents, DeleteSharedItem, RateSharedItem, SignUpSharedProject, AddPins, RemovePins, ViewSharedItem, SharedItemComments, CreateSharedItemComment, UpdateSharedItemComment, DeleteSharedItemComment, Myself, UpdateSharedItem"
    >
      <div className="mb-5">
        <BackButton />
      </div>
      {loading ? (
        <div className="text-[11px] text-ink/45">
          <FormattedMessage id="share.itemDetail.loading" defaultMessage="loading…" />
        </div>
      ) : error ? (
        <div className="text-[11px] text-ink/45">
          <FormattedMessage
            id="share.itemDetail.error"
            defaultMessage="Error: {message}"
            values={{ message: userErrorMessage(intl, error) }}
          />
        </div>
      ) : !item ? (
        <NoBackend
          label={intl.formatMessage({ id: "share.itemDetail.notFound", defaultMessage: "Post not found" })}
        />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-6 @lg:grid-cols-[minmax(220px,280px)_minmax(0,1fr)_minmax(260px,320px)] @lg:items-start">
            {/* LEFT - Meta */}
            <aside>
              <div className="box bg-surface p-5">
                <div className="space-y-4">
                  <div>
                    {/* WHOSE POST THIS IS. The content belongs to the company and the company
                        answers for it; the line survives the author's erasure, because it comes
                        from `company_id` and not from them. A personal post shows nothing here —
                        not an empty "Published by".

                        Concept v10 (0002 §6) asks for the author beneath it as well, "both, not
                        instead", and that is what happens — for every reader. @adr SPARK-0042-company-post-detail-shows-company-and-operator@1 supersedes
                        the short-lived admin-only rule. See the note below the company block. */}
                    {item.company ? (
                      <div className="mb-2">
                        <div className="mb-1 text-[10px] tracking-[0.25em] text-ink/45">
                          <FormattedMessage
                            id="share.itemDetail.publishedBy"
                            defaultMessage="Published by"
                          />
                        </div>
                        <Link
                          to={`/c/${item.company.id}`}
                          className="flex items-center gap-2 text-sm font-medium underline-offset-2 hover:underline"
                        >
                          {/* The same component the company page and the company list draw, so the
                              monogram fallback for a company with no logo is decided in one place
                              (0006 §1) rather than invented a third time here. */}
                          <CompanyLogo logo={item.company.logo} name={item.company.name} size="row" />
                          <span className="min-w-0 truncate">{item.company.name}</span>
                        </Link>
                      </div>
                    ) : null}
                    {/* A COMPANY'S POST NAMES ITS OPERATOR, QUIETLY, TO EVERYONE WHO CAN SEE THE
                        POST. One small line under the company — no avatar and no eyebrow of its own,
                        because it is provenance rather than a second author of equal standing — and
                        it is gated on `publisher` alone, with no viewer test.

                        REVERSED ON 2026-08-31 (Kevin: "the author of a company's post should be
                        visible to everyone able to see the company who published it"). @adr SPARK-0042-company-post-detail-shows-company-and-operator@1
                        records that decision and supersedes Spark's short-lived admin-only rule. Do
                        not reintroduce a viewer gate here without a new superseding ADR.

                        A PERSONAL post is untouched: the author IS the publisher there, so it keeps
                        the label and the face. */}
                    {publisher ? (
                      <p className="field-meta text-ink/55">
                        {author ? (
                          <Link
                            to={`/p/${author.id}`}
                            className="underline-offset-2 hover:underline"
                          >
                            <FormattedMessage
                              id="share.itemDetail.postedByOperator"
                              defaultMessage="Posted by {name}"
                              values={{ name: authorName }}
                            />
                          </Link>
                        ) : (
                          <FormattedMessage
                            id="share.itemDetail.postedByOperator"
                            defaultMessage="Posted by {name}"
                            values={{ name: authorName }}
                          />
                        )}
                      </p>
                    ) : (
                      <>
                        <div className="mb-1 text-[10px] tracking-[0.25em] text-ink/45">
                          <FormattedMessage id="share.itemDetail.author" defaultMessage="Author" />
                        </div>
                        <SharedItemAuthor
                          name={authorName}
                          person={
                            author
                              ? {
                                  id: author.id,
                                  avatarContent: authorAvatar.data?.person?.avatar?.content,
                                  avatarMime: authorAvatar.data?.person?.avatar?.mimeType,
                                }
                              : null
                          }
                        />
                      </>
                    )}
                  </div>
                  <div>
                    <div className="mb-1 text-[10px] tracking-[0.25em] text-ink/45">
                      <FormattedMessage id="share.itemDetail.publishedOn" defaultMessage="Published on" />
                    </div>
                    <div className="text-sm">{formatDateTime(item.publishedOn, intl.locale) || "–"}</div>
                  </div>
                  <RatingBlock
                    item={item}
                    rate={async (stars) => {
                      setActionError(null);
                      setDeleteError(null);
                      try {
                        await sharedItem.rate(stars);
                      } catch (e) {
                        // The stars snap back to zero on a rejection, which without a message reads
                        // as "the click was lost" rather than "the rating was refused".
                        // The reader's wording: rating is not an admin act either.
                        setActionError(
                          companyFrozenReaderMessage(intl, e) ??
                            userErrorMessage(intl, e),
                        );
                        throw e;
                      }
                    }}
                    onRated={() => void sharedItem.refetch()}
                  />
                  {/* WHERE THE REFUSAL WAS RAISED. Sign-up and rating are both aside controls, so
                      their one shared error line belongs under them rather than in the middle
                      column's toolbar. Neutral text, not a warning ring: a refused rating is a
                      statement about the company's state, not a fault of the reader's
                      (docs/UI_CONVENTIONS.md). */}
                  {actionError ? (
                    <p className="text-[10px] text-ink/70">{actionError}</p>
                  ) : null}
                </div>

                <div
                  className="mt-5 space-y-2.5 border-t border-ink/5 pt-4"
                  data-assistant-anchor="share.item.stats"
                >
                  <Stat
                    icon="eye"
                    label={intl.formatMessage({ id: "share.itemDetail.statViews", defaultMessage: "Views" })}
                    value={item.views}
                  />
                  <Stat
                    icon="pin"
                    label={intl.formatMessage({ id: "share.itemDetail.statPins", defaultMessage: "Pins" })}
                    value={item.watches}
                  />
                  <Stat
                    icon="hand"
                    label={intl.formatMessage({ id: "share.itemDetail.statInterested", defaultMessage: "Interested" })}
                    value={item.signUps}
                  />
                </div>

                <div className="mt-5 space-y-2" data-assistant-anchor="share.item.actions">
                  <Button
                    variant="primary"
                    onClick={() => void toggleWatch()}
                    disabled={watching}
                    className="w-full disabled:opacity-50"
                  >
                    {watching ? (
                      "…"
                    ) : item.pinned ? (
                      <FormattedMessage
                        id="share.itemDetail.unwatch"
                        defaultMessage="✓ Watching - remove"
                      />
                    ) : (
                      <FormattedMessage id="share.itemDetail.watch" defaultMessage="Watch" />
                    )}
                  </Button>
                  {!mayManageItem(item) && item.itemType === "PROJECT" ? (
                    <Button
                      variant="primary"
                      onClick={() => void toggleSignUp()}
                      disabled={signingUp}
                      className="w-full disabled:opacity-50"
                    >
                      {signingUp ? (
                        "…"
                      ) : item.signedUp ? (
                        <FormattedMessage
                          id="share.itemDetail.signedUpWithdraw"
                          defaultMessage="✓ Interested - withdraw"
                        />
                      ) : (
                        <FormattedMessage id="share.itemDetail.expressInterest" defaultMessage="Express interest" />
                      )}
                    </Button>
                  ) : null}
                  <Button
                    variant="primary"
                    onClick={() => setNewMatchOpen(true)}
                    className="w-full disabled:opacity-50"
                    title={intl.formatMessage({
                      id: "share.itemDetail.linkedMatchTitle",
                      defaultMessage: "Start a new match linked to this share item",
                    })}
                  >
                    <FormattedMessage id="share.itemDetail.newMatch" defaultMessage="New match ↗" />
                  </Button>
                  {newMatchOpen && (
                    <NewMatchModal
                      onCreate={startLinkedMatch}
                      onClose={() => setNewMatchOpen(false)}
                    />
                  )}
                </div>
              </div>
            </aside>

            {/* MIDDLE - Content */}
            <section data-assistant-anchor="share.item.content">
              <div className="box bg-surface p-5 @sm:p-6">
                <div className="flex items-start justify-between gap-3">
                  <span className="inline-block rounded bg-accent px-1.5 py-0.5 text-[9px] uppercase tracking-widest text-on-accent">
                    {itemTypeLabel(item.itemType)}
                  </span>
                  <div className="-mt-1 flex items-center gap-1.5">
                    <ShareUrlButton
                      path={`/share/discover/${item.id}`}
                      title={intl.formatMessage({ id: "share.itemDetail.share", defaultMessage: "Share" })}
                      className={buttonClasses({ variant: "secondary", size: "compact" })}
                    >
                      <FormattedMessage id="share.itemDetail.shareWithIcon" defaultMessage="↗ Share" />
                    </ShareUrlButton>
                    {/* DELETE'S REFUSAL ONLY. Sign-up and rating live in the aside, and their
                        refusals used to surface here — a sentence about a rating appearing beside
                        Share/Edit/Delete, a column away from the stars that raised it, which reads
                        as an unrelated failure. `actionError` now renders under the block that
                        raised it; this one stays because Delete is right here. */}
                    {deleteError ? (
                      <span className="text-[10px] text-ink/70">{deleteError}</span>
                    ) : null}
                    {mayManageItem(item) && confirmingDelete ? (
                      <>
                        <span className="text-[10px] text-ink/55">
                          <FormattedMessage id="share.itemDetail.deleteConfirm" defaultMessage="Delete?" />
                        </span>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => void runDelete()}
                          disabled={deleting}
                        >
                          <FormattedMessage id="share.itemDetail.yes" defaultMessage="Yes" />
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setConfirmingDelete(false)}
                        >
                          <FormattedMessage id="share.itemDetail.no" defaultMessage="No" />
                        </Button>
                      </>
                    ) : null}
                    {mayManageItem(item) && !confirmingDelete ? (
                      <>
                        <ActionBtn
                          icon="pencil"
                          title={intl.formatMessage({ id: "common.edit", defaultMessage: "Edit" })}
                          onClick={() => navigate(`/share/edit/${item.id}`)}
                        />
                        <ActionBtn
                          icon="trash"
                          title={intl.formatMessage({ id: "common.delete", defaultMessage: "Delete" })}
                          danger
                          onClick={() => setConfirmingDelete(true)}
                        />
                      </>
                    ) : null}
                  </div>
                </div>
                <h1 className="mt-3 font-serif text-3xl leading-tight @sm:text-4xl">{item.name || "–"}</h1>
                {item.shortDescription ? (
                  <p className="mt-3 max-w-2xl text-sm leading-relaxed text-ink/70">{item.shortDescription}</p>
                ) : null}

                {hero ? (
                  <div className="mt-5">
                    <div className="overflow-hidden rounded-md bg-ink/[0.06] ring-1 ring-ink/5">
                      <img
                        src={imgSrc(hero) ?? ""}
                        alt={item.name || ""}
                        className="aspect-[16/9] w-full object-cover"
                      />
                    </div>
                    {gallery.length > 1 ? (
                      <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
                        {gallery.map((f, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => setActiveIdx(i)}
                            title={intl.formatMessage(
                              { id: "share.itemDetail.imageN", defaultMessage: "Image {n}" },
                              { n: i + 1 },
                            )}
                            className={`shrink-0 overflow-hidden rounded-md ring-1 transition ${
                              i === activeIdx ? "ring-accent" : "ring-ink/10 hover:ring-accent/40"
                            }`}
                          >
                            <img src={imgSrc(f) ?? ""} alt="" className="h-16 w-24 object-cover" />
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ) : null}

                <div className="mt-6 border-t border-ink/5 pt-5">
                  {item.description ? (
                    <div className="whitespace-pre-wrap text-sm leading-relaxed text-ink/80">
                      {item.description}
                    </div>
                  ) : (
                    <NoBackend
                      label={intl.formatMessage({ id: "share.itemDetail.noDescription", defaultMessage: "No description" })}
                      hint={intl.formatMessage({ id: "share.itemDetail.noDescriptionHint", defaultMessage: "no content provided" })}
                    />
                  )}
                </div>

                <ShareItemAttachments
                  files={item.files ?? []}
                  onOpen={sharedItem.openFile}
                  onDownload={sharedItem.downloadFile}
                />
              </div>
            </section>

            {/* RIGHT - Comments */}
            <aside className="@lg:sticky @lg:top-6">
              <CommentArea itemId={item.id} />
            </aside>
          </div>

        </>
      )}
    </main>
  );
}

function Stat({
  icon,
  label,
  value,
}: {
  icon: ShareIconName;
  label: string;
  value: number | null | undefined;
}) {
  return (
    <div className="flex items-center gap-2.5 text-[13px]">
      <span className="w-6 tabular-nums text-ink">{Number(value ?? 0)}</span>
      <ShareIcon name={icon} className="h-5 w-5 text-ink/55" />
      <span className="text-ink/55">{label}</span>
    </div>
  );
}

function ActionBtn({
  icon,
  title,
  danger = false,
  onClick,
  disabled = false,
  size = "md",
}: {
  icon: ShareIconName;
  title: string;
  danger?: boolean;
  onClick: () => void;
  disabled?: boolean;
  size?: "sm" | "md";
}) {
  const dim = size === "sm" ? "h-7 w-7" : "h-9 w-9";
  const ico = size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4";
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex ${dim} items-center justify-center rounded-full ring-1 ring-ink/15 transition disabled:opacity-40 ${
        danger
          ? "text-ink/55 hover:bg-red-600 hover:text-white hover:ring-red-600"
          : "text-ink/70 hover:bg-ink hover:text-on-ink"
      }`}
    >
      <ShareIcon name={icon} className={ico} />
    </button>
  );
}

function RatingBlock({
  item,
  rate,
  onRated,
}: {
  item: ShareItemLike;
  rate: (fiveStarRating: number) => Promise<void>;
  onRated: () => void;
}) {
  const intl = useIntl();
  // Not just "somebody else wrote it": an admin must not rate their own company's content
  // either, and genesis refuses it silently, so offering the control would be a dead end.
  const canRate = !mayManageItem(item);
  const [open, setOpen] = useState(false);

  return (
    <div>
      <div className="mb-1.5 text-[10px] tracking-[0.25em] text-ink/45">
        <FormattedMessage id="share.itemDetail.rating" defaultMessage="Rating" />
      </div>
      <button
        type="button"
        onClick={canRate ? () => setOpen((o) => !o) : undefined}
        title={
          canRate
            ? intl.formatMessage({ id: "share.itemDetail.rateYourselfTitle", defaultMessage: "Click to rate it yourself" })
            : undefined
        }
        className={`group flex items-center gap-2 text-sm ${canRate ? "cursor-pointer" : "cursor-default"}`}
      >
        <ShareIcon name="star" className="h-5 w-5 text-ink" />
        <span className="font-medium tabular-nums">{fiveStarRounded(item.rating)}</span>
        <span className="text-ink/55">
          <FormattedMessage
            id="share.itemDetail.ratingsCount"
            defaultMessage="({count} ratings)"
            values={{ count: Number(item.ratings ?? 0) }}
          />
        </span>
        {canRate ? (
          <span className="text-[10px] tracking-[0.15em] text-ink/30 transition group-hover:text-accent">
            {open ? (
              <FormattedMessage id="share.itemDetail.ratingClose" defaultMessage="close" />
            ) : (
              <FormattedMessage id="share.itemDetail.ratingOpen" defaultMessage="rate ›" />
            )}
          </span>
        ) : null}
      </button>
      {canRate && open ? (
        <div className="mt-2.5">
          <RateStars rate={rate} alreadyRated={Boolean(item.rated)} onRated={onRated} />
        </div>
      ) : null}
    </div>
  );
}

function RateStars({
  rate,
  alreadyRated,
  onRated,
}: {
  rate: (fiveStarRating: number) => Promise<void>;
  alreadyRated: boolean;
  onRated: () => void;
}) {
  const intl = useIntl();
  const [loading, setLoading] = useState(false);
  const [hover, setHover] = useState(0);
  const [mine, setMine] = useState(0);
  const shown = hover || mine;

  async function submit(n: number) {
    if (loading) return;
    setMine(n);
    setLoading(true);
    try {
      await rate(n);
      onRated();
    } catch {
      logApplicationFailure("share.rate-item");
      setMine(0);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <div className="flex items-center gap-1" onMouseLeave={() => setHover(0)}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            disabled={loading}
            onMouseEnter={() => setHover(n)}
            onClick={() => void submit(n)}
            title={intl.formatMessage(
              { id: "share.itemDetail.starsTitle", defaultMessage: "{n, plural, one {# star} other {# stars}}" },
              { n },
            )}
            aria-label={intl.formatMessage(
              { id: "share.itemDetail.starsAriaLabel", defaultMessage: "{n} of 5 stars" },
              { n },
            )}
            className="text-xl leading-none transition disabled:opacity-50"
          >
            <span className={n <= shown ? "text-ink" : "text-ink/30"}>★</span>
          </button>
        ))}
        {mine > 0 ? (
          <span className="ml-2 text-[11px] text-ink/45">
            <FormattedMessage id="share.itemDetail.thanks" defaultMessage="Thanks!" />
          </span>
        ) : null}
      </div>
      {alreadyRated && mine === 0 ? (
        <div className="mt-1 text-[10px] text-ink/40">
          <FormattedMessage
            id="share.itemDetail.alreadyRated"
            defaultMessage="You have already rated, tap again to change."
          />
        </div>
      ) : null}
    </div>
  );
}

function CommentArea({ itemId }: { itemId: string }) {
  const intl = useIntl();
  // NO COMMENTING WHILE ACTING AS THE COMPANY (concept v10, 0002 §6). A comment is a person's
  // utterance and there is no company voice for one — genesis stores `shared_item_comment.person_id`
  // and nothing else — so a comment written in company context would silently be attributed to the
  // human behind it, which is the opposite of what the context says they are doing. §6 bounds the
  // context to viewing and editing the company, managing members and posting in its name; commenting
  // is not in that list.
  //
  // The thread stays READABLE. What is withheld is the composer, and it is absent rather than
  // disabled: leaving the company context is the way to comment, and the note says so.
  const { active: actingAsCompany } = useCompanyContext();
  const commentsHook = useSharedItemComments(itemId);
  const { comments, totalCount: total, loading, error, busy } = commentsHook;
  const { data: myselfData } = useQuery(MyselfDocument);
  const meId = myselfData?.myself?.id ?? null;

  const [text, setText] = useState("");

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!text.trim() || busy) return;
    try {
      await commentsHook.add(text);
      setText("");
    } catch {
      logApplicationFailure("share.create-comment");
    }
  }

  async function onDelete(id: string) {
    try {
      await commentsHook.remove(id);
    } catch {
      logApplicationFailure("share.delete-comment");
    }
  }

  return (
    <div className="box flex flex-col overflow-hidden bg-surface">
      <div className="flex items-center justify-between gap-2 border-b border-ink/5 px-4 py-3">
        <div className="text-[10px] tracking-[0.25em] text-ink/55">
          <FormattedMessage id="share.itemDetail.comments" defaultMessage="Comments" />
        </div>
        <span className="text-[11px] tabular-nums text-ink/40">{total}</span>
      </div>

      {actingAsCompany ? (
        <div className="border-b border-ink/5 p-3 text-[11px] text-ink/55">
          <FormattedMessage
            id="share.itemDetail.commentAsCompanyBlocked"
            defaultMessage="You are acting as {company}. Leave the company context to comment as yourself."
            values={{ company: <span className="font-medium">{actingAsCompany.name}</span> }}
          />
        </div>
      ) : (
      <form onSubmit={submit} className="border-b border-ink/5 p-3">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={2}
          placeholder={intl.formatMessage({ id: "share.itemDetail.commentPlaceholder", defaultMessage: "Write a comment…" })}
          className="w-full resize-none rounded-md bg-page px-3 py-2 text-[13px] leading-relaxed outline-none ring-1 ring-ink/10 transition focus:ring-accent/40"
        />
        <div className="mt-2 flex justify-end">
          <Button
            type="submit"
            variant="primary"
            disabled={busy || !text.trim()}
            className="px-4 py-1.5 disabled:opacity-40"
          >
            {busy ? "…" : <FormattedMessage id="share.itemDetail.send" defaultMessage="Send" />}
          </Button>
        </div>
      </form>
      )}

      <div className="max-h-[60vh] min-h-[18rem] space-y-4 overflow-y-auto px-4 py-4">
        {loading ? (
          <div className="text-[11px] text-ink/45">
            <FormattedMessage id="share.itemDetail.loading" defaultMessage="loading…" />
          </div>
        ) : error ? (
          <div className="text-[11px] text-ink/45">
            <FormattedMessage
              id="share.itemDetail.error"
              defaultMessage="Error: {message}"
              values={{ message: userErrorMessage(intl, error) }}
            />
          </div>
        ) : comments.length === 0 ? (
          <div className="text-[12px] text-ink/45">
            <FormattedMessage id="share.itemDetail.noComments" defaultMessage="No comments yet, be the first." />
          </div>
        ) : (
          comments.map((c) => (
            <CommentRow
              key={c.id}
              comment={c}
              mine={Boolean(meId && c.person?.id === meId)}
              busy={busy}
              onEdit={(content) => commentsHook.edit(c.id, content)}
              onDelete={() => void onDelete(c.id)}
            />
          ))
        )}
      </div>
    </div>
  );
}

function CommentRow({
  comment,
  mine = false,
  busy = false,
  onEdit,
  onDelete,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  comment: any;
  mine?: boolean;
  busy?: boolean;
  onEdit: (content: string) => Promise<void>;
  onDelete: () => void;
}) {
  const intl = useIntl();
  const [menuOpen, setMenuOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string>(comment.content ?? "");
  const [saving, setSaving] = useState(false);

  async function submitEdit() {
    const trimmed = draft.trim();
    if (!trimmed || saving) return;
    if (trimmed === (comment.content ?? "").trim()) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onEdit(trimmed);
      setEditing(false);
    } catch {
      logApplicationFailure("share.edit-comment");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[12px] font-medium">{comment.person?.formattedName || "–"}</span>
        <div className="flex shrink-0 items-center gap-1">
          <span className="text-[10px] text-ink/40">{formatDateTime(comment.postedAt, intl.locale) || "–"}</span>
          {mine ? (
            <div className="relative">
              <button
                type="button"
                onClick={() => setMenuOpen((o) => !o)}
                title={intl.formatMessage({ id: "share.itemDetail.more", defaultMessage: "More" })}
                aria-label={intl.formatMessage({ id: "share.itemDetail.more", defaultMessage: "More" })}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                className="-mr-1 inline-flex h-6 w-6 items-center justify-center rounded-full text-ink/40 transition hover:bg-ink/[0.06] hover:text-ink"
              >
                <ShareIcon name="dots" className="h-4 w-4" />
              </button>
              {menuOpen ? (
                <>
                  <div
                    aria-hidden="true"
                    onClick={() => setMenuOpen(false)}
                    className="fixed inset-0 z-30"
                  />
                  <div
                    role="menu"
                    className="absolute right-0 top-full z-40 mt-1 w-36 overflow-hidden rounded-md bg-surface py-1 shadow-lg ring-1 ring-ink/10"
                  >
                    <MenuItem
                      role="menuitem"
                      icon={<ShareIcon name="pencil" className="h-3.5 w-3.5" />}
                      onClick={() => {
                        setMenuOpen(false);
                        setDraft(comment.content ?? "");
                        setEditing(true);
                      }}
                    >
                      <FormattedMessage id="common.edit" defaultMessage="Edit" />
                    </MenuItem>
                    <MenuItem
                      role="menuitem"
                      icon={<ShareIcon name="trash" className="h-3.5 w-3.5" />}
                      disabled={busy}
                      onClick={() => {
                        setMenuOpen(false);
                        onDelete();
                      }}
                      className="text-red-600 hover:text-red-700"
                    >
                      <FormattedMessage id="common.delete" defaultMessage="Delete" />
                    </MenuItem>
                  </div>
                </>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      {editing ? (
        <div className="mt-1.5">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={3}
            autoFocus
            className="w-full resize-none rounded-md bg-page px-3 py-2 text-[13px] leading-relaxed outline-none ring-1 ring-ink/10 transition focus:ring-accent/40"
          />
          <div className="mt-2 flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => setEditing(false)}
              disabled={saving}
              className="px-3 py-1.5 text-[10px] disabled:opacity-40"
            >
              <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
            </Button>
            <Button
              variant="primary"
              onClick={() => void submitEdit()}
              disabled={saving || busy || !draft.trim()}
              className="px-3 py-1.5 text-[10px] disabled:opacity-40"
            >
              {saving ? "…" : <FormattedMessage id="common.save" defaultMessage="Save" />}
            </Button>
          </div>
        </div>
      ) : (
        <p className="mt-0.5 whitespace-pre-wrap text-[13px] leading-relaxed text-ink/80">{comment.content}</p>
      )}
    </div>
  );
}
